import { useState, useCallback, useEffect } from "react";

interface UseListFiltersOptions<T> {
  /** Unique key for localStorage persistence */
  storageKey: string;
  /** Default filter values */
  defaults: T;
  /** Whether to persist filters to localStorage */
  persist?: boolean;
}

interface UseListFiltersReturn<T> {
  /** Current filter values */
  filters: T;
  /** Update a single filter value */
  setFilter: <K extends keyof T>(key: K, value: T[K]) => void;
  /** Update multiple filter values at once */
  setFilters: (updates: Partial<T>) => void;
  /** Reset all filters to defaults */
  resetFilters: () => void;
  /** Check if any filters differ from defaults */
  hasActiveFilters: boolean;
  /** Count of active (non-default) filters */
  activeFilterCount: number;
}

/**
 * Hook for managing list filters with optional localStorage persistence.
 *
 * Features:
 * - Remembers filter state across page navigation
 * - Persists to localStorage (optional, default: true)
 * - Tracks active filter count for UI indicators
 * - Type-safe filter updates
 *
 * @example
 * const { filters, setFilter, resetFilters, hasActiveFilters } = useListFilters({
 *   storageKey: "caseListFilters",
 *   defaults: { status: "open", searchTerm: "" },
 * });
 */
export function useListFilters<T extends object>({
  storageKey,
  defaults,
  persist = true,
}: UseListFiltersOptions<T>): UseListFiltersReturn<T> {
  // Initialize from localStorage or defaults
  const [filters, setFiltersState] = useState<T>(() => {
    if (!persist) return defaults;

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to handle new filter keys
        return { ...defaults, ...parsed };
      }
    } catch (e) {
      console.warn(`Failed to load filters from localStorage: ${storageKey}`, e);
    }
    return defaults;
  });

  // Persist to localStorage when filters change
  useEffect(() => {
    if (!persist) return;

    try {
      localStorage.setItem(storageKey, JSON.stringify(filters));
    } catch (e) {
      console.warn(`Failed to save filters to localStorage: ${storageKey}`, e);
    }
  }, [filters, storageKey, persist]);

  // Update a single filter
  const setFilter = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setFiltersState((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Update multiple filters at once
  const setFilters = useCallback((updates: Partial<T>) => {
    setFiltersState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Reset to defaults
  const resetFilters = useCallback(() => {
    setFiltersState(defaults);
  }, [defaults]);

  // Calculate active filter count
  const activeFilterCount = Object.keys(defaults).filter((key) => {
    const current = filters[key as keyof T];
    const defaultVal = defaults[key as keyof T];

    // Handle arrays
    if (Array.isArray(current) && Array.isArray(defaultVal)) {
      return JSON.stringify(current) !== JSON.stringify(defaultVal);
    }

    // Handle empty strings as equivalent to undefined for search fields
    if (current === "" && defaultVal === "") return false;

    return current !== defaultVal;
  }).length;

  const hasActiveFilters = activeFilterCount > 0;

  return {
    filters,
    setFilter,
    setFilters,
    resetFilters,
    hasActiveFilters,
    activeFilterCount,
  };
}

export default useListFilters;
